// User password policy — FE mirror of cpac-isupply-api app/PasswordPolicy.php
// (TECH_SPEC_user_password_policy.md §1). The server is the source of truth:
// these checks only give early feedback. Reuse (`reused`) needs the stored
// password history, so only the server can report it.

export const MIN_LENGTH = 8
export const MAX_LENGTH = 64
export const MIN_CHARACTER_CLASSES = 3
export const HISTORY_SIZE = 4
export const NOTIFY_DAYS = 15

// exactly the set in the policy table — any other character is invalid
export const SPECIAL_CHARACTERS = '!@#$%^&*()_+|~-=\\`{}[]:";\'<>?,./'

export const VIOLATION_MIN_LENGTH = 'min_length'
export const VIOLATION_MAX_LENGTH = 'max_length'
export const VIOLATION_INVALID_CHARACTERS = 'invalid_characters'
export const VIOLATION_COMPLEXITY = 'complexity'
export const VIOLATION_REUSED = 'reused'

// login refused because of the password (HTTP 403 `error`, TECH_SPEC §3.1)
export const PASSWORD_BLOCK_REASONS = ['password_change_required', 'password_expired']
// account lockout: also HTTP 403, but NOT a forced-change case — only an admin unlocks
export const ACCOUNT_LOCKED_REASON = 'account_locked'
export const ACCOUNT_LOCKED_MESSAGE =
  'บัญชีถูกล็อกเนื่องจากใส่รหัสผ่านผิดครบ 5 ครั้ง กรุณาติดต่อผู้ดูแลระบบ'
// who may unlock (BE PasswordPolicy::UNLOCK_PERMISSION_NAME / _ACTION)
// special_4 of the "user" permission — special_1-3 are already Export / Export CSV / Import user
export const UNLOCK_PERMISSION = { name: 'user', action: 'can_special_4' }
// auth.status while the login page shows the forced change view
export const PASSWORD_CHANGE_STATUS = 'password_change'
// auth.status while the login page shows the "account locked" view (15/10/2026)
export const ACCOUNT_LOCKED_STATUS = 'account_locked'
// wrong passwords in a row before the lock (BE PasswordPolicy::LOCKOUT_THRESHOLD)
export const LOCKOUT_THRESHOLD = 5
// wrong password on an iSupply-password account: the 400 body says how many tries
// are left before the lock (BE PasswordPolicy::REMAINING_ATTEMPTS_FIELD · 16/10/2026)
export const REMAINING_ATTEMPTS_FIELD = 'remaining_attempts'

/** the count from a 400 login answer; anything that is not a positive whole number → null */
export function remainingAttemptsOf(response) {
  const value = response && response.data ? response.data[REMAINING_ATTEMPTS_FIELD] : undefined
  return Number.isInteger(value) && value > 0 ? value : null
}

export const remainingAttemptsMessage = (remaining) =>
  `ใส่รหัสผ่านผิดได้อีก ${remaining} ครั้ง ก่อนบัญชีจะถูกล็อก`

// user master edit page: password refused because the account is locked
// (BE PasswordPolicy::STATUS_LOCKED_PASSWORD_EDIT — DECISIONS #16)
export const LOCKED_PASSWORD_EDIT_STATUS = 'fail_account_locked'
// window event fired after a successful change (Header banner refetches)
export const PASSWORD_CHANGED_EVENT = 'password-changed'

export const PASSWORD_VIOLATION_MESSAGES = {
  [VIOLATION_MIN_LENGTH]: `รหัสผ่านต้องมีอย่างน้อย ${MIN_LENGTH} ตัวอักษร`,
  [VIOLATION_MAX_LENGTH]: `รหัสผ่านต้องยาวไม่เกิน ${MAX_LENGTH} ตัวอักษร`,
  [VIOLATION_INVALID_CHARACTERS]:
    'ใช้ได้เฉพาะ A-Z, a-z, 0-9 และอักขระพิเศษที่กำหนด (ห้ามภาษาไทยหรือช่องว่าง)',
  [VIOLATION_COMPLEXITY]:
    'ต้องมีอย่างน้อย 3 จาก 4 ประเภท: ตัวพิมพ์ใหญ่ (A-Z) ตัวพิมพ์เล็ก (a-z) ตัวเลข (0-9) อักขระพิเศษ',
  [VIOLATION_REUSED]: `ห้ามใช้รหัสผ่านซ้ำกับ ${HISTORY_SIZE} ครั้งล่าสุด`,
}

// code points, like PHP mb_strlen / preg_split('//u') on the server
const toChars = (password) => Array.from(password == null ? '' : String(password))

const isUpper = (ch) => /^[A-Z]$/.test(ch)
const isLower = (ch) => /^[a-z]$/.test(ch)
const isDigit = (ch) => /^[0-9]$/.test(ch)
const isSpecial = (ch) => ch.length === 1 && SPECIAL_CHARACTERS.indexOf(ch) !== -1

/** Which of the 4 character classes the password contains. */
export function characterClassesOf(password) {
  const classes = { upper: false, lower: false, digit: false, special: false }
  toChars(password).forEach((ch) => {
    if (isUpper(ch)) classes.upper = true
    else if (isLower(ch)) classes.lower = true
    else if (isDigit(ch)) classes.digit = true
    else if (isSpecial(ch)) classes.special = true
  })
  return classes
}

export function hasInvalidCharacters(password) {
  return toChars(password).some(
    (ch) => !(isUpper(ch) || isLower(ch) || isDigit(ch) || isSpecial(ch))
  )
}

export function passwordLength(password) {
  return toChars(password).length
}

/**
 * Format violations, always in this order:
 * min_length · max_length · invalid_characters · complexity
 */
export function passwordViolations(password) {
  const violations = []
  const length = passwordLength(password)
  if (length < MIN_LENGTH) violations.push(VIOLATION_MIN_LENGTH)
  if (length > MAX_LENGTH) violations.push(VIOLATION_MAX_LENGTH)
  if (hasInvalidCharacters(password)) violations.push(VIOLATION_INVALID_CHARACTERS)
  const classes = characterClassesOf(password)
  const classCount = Object.keys(classes).filter((k) => classes[k]).length
  if (classCount < MIN_CHARACTER_CLASSES) violations.push(VIOLATION_COMPLEXITY)
  return violations
}

export function passwordViolationMessage(code) {
  return PASSWORD_VIOLATION_MESSAGES[code] || code
}

/** Thai messages for server `violations` (unknown codes pass through). */
export function passwordViolationMessages(codes) {
  return Array.isArray(codes) ? codes.map(passwordViolationMessage) : []
}

// live checklist: an empty password meets nothing yet
export const PASSWORD_RULES = [
  {
    key: 'length',
    label: `ความยาว ${MIN_LENGTH}-${MAX_LENGTH} ตัวอักษร`,
    isMet: (pw) => {
      const length = passwordLength(pw)
      return length >= MIN_LENGTH && length <= MAX_LENGTH
    },
  },
  {
    key: 'characters',
    label: 'ใช้ได้เฉพาะ A-Z, a-z, 0-9 และอักขระพิเศษ (ห้ามภาษาไทยหรือช่องว่าง)',
    isMet: (pw) => passwordLength(pw) > 0 && !hasInvalidCharacters(pw),
  },
  {
    key: 'complexity',
    label: 'มีอย่างน้อย 3 จาก 4 ประเภทด้านล่าง',
    isMet: (pw) => {
      const classes = characterClassesOf(pw)
      return Object.keys(classes).filter((k) => classes[k]).length >= MIN_CHARACTER_CLASSES
    },
  },
]

export const CHARACTER_CLASS_RULES = [
  { key: 'upper', label: 'ตัวพิมพ์ใหญ่ (A-Z)' },
  { key: 'lower', label: 'ตัวพิมพ์เล็ก (a-z)' },
  { key: 'digit', label: 'ตัวเลข (0-9)' },
  { key: 'special', label: `อักขระพิเศษ ${SPECIAL_CHARACTERS.split('').join(' ')}` },
]

/** All format messages joined, or null when the password passes. */
export function passwordPolicyErrorMessage(password) {
  const violations = passwordViolations(password)
  return violations.length ? passwordViolationMessages(violations).join(' / ') : null
}

/**
 * yup `.test()` options for a password field. Empty is left to `.required()`
 * (Formik 1.x turns '' into undefined before validating).
 * `prefix` keeps the legacy forms' `' ' + msg` convention: they display
 * `msg.substr(msg.indexOf(' ') + 1)`.
 */
export function passwordPolicyTest(prefix = '') {
  return {
    name: 'password-policy',
    message: prefix + passwordViolationMessage(VIOLATION_COMPLEXITY),
    test(value) {
      if (value === undefined || value === null || value === '') return true
      const message = passwordPolicyErrorMessage(value)
      return message === null ? true : this.createError({ message: prefix + message })
    },
  }
}

/**
 * Locked according to GET /masterdatauser/lock-status/{id} (authenticated). Only a
 * successful answer for a type-1 account (`applies`) with `locked: true` counts —
 * 403 / error / missing payload = not shown as locked.
 */
export function isLockStatusLocked(lockStatus) {
  return (
    !!lockStatus &&
    lockStatus.status === 'success' &&
    lockStatus.applies === true &&
    lockStatus.locked === true
  )
}

/** '2026-10-14 09:05:33' → '14/10/2026 09:05' (server Asia/Bangkok time, no timezone math). */
export function formatLockedAt(lockedAt) {
  const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(lockedAt || '')
  return match ? `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}` : ''
}

/** '2026-11-13 10:00:00' → '13/11/2026' (server date, no timezone math). */
export function formatPasswordExpiryDate(expiredAt) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(expiredAt || '')
  return match ? `${match[3]}/${match[2]}/${match[1]}` : ''
}

// ---- temporary password for an admin unlock (user 15/10/2026: the system
// makes it up so the admin does not have to) ----------------------------------
// 12 characters with all 4 classes (the policy needs 3) · no look-alikes
// (0 O o 1 l I) · a special set that is easy to read out / paste into chat ·
// always passes passwordViolations(). Browser crypto only — never Math.random.
export const TEMP_PASSWORD_LENGTH = 12
export const TEMP_PASSWORD_SETS = {
  upper: 'ABCDEFGHJKLMNPQRSTUVWXYZ',
  lower: 'abcdefghijkmnpqrstuvwxyz',
  digit: '23456789',
  special: '!@#$%&*?+=',
}

function secureRandomInt(max) {
  const cryptoObj = typeof window !== 'undefined' ? window.crypto || window.msCrypto : null
  if (!cryptoObj || typeof cryptoObj.getRandomValues !== 'function') {
    throw new Error('crypto.getRandomValues is not available')
  }
  // rejection sampling: no modulo bias
  const limit = Math.floor(0x100000000 / max) * max
  const buffer = new Uint32Array(1)
  do {
    cryptoObj.getRandomValues(buffer)
  } while (buffer[0] >= limit)
  return buffer[0] % max
}

const pickFrom = (chars) => chars[secureRandomInt(chars.length)]

export function generateTemporaryPassword(length = TEMP_PASSWORD_LENGTH) {
  const sets = TEMP_PASSWORD_SETS
  const all = sets.upper + sets.lower + sets.digit + sets.special
  const chars = [pickFrom(sets.upper), pickFrom(sets.lower), pickFrom(sets.digit), pickFrom(sets.special)]
  while (chars.length < length) chars.push(pickFrom(all))
  // Fisher–Yates so the guaranteed classes are not always up front
  for (let i = chars.length - 1; i > 0; i--) {
    const j = secureRandomInt(i + 1)
    const swap = chars[i]
    chars[i] = chars[j]
    chars[j] = swap
  }
  return chars.join('')
}

/**
 * Copy to the clipboard: the Clipboard API on https / localhost; when it is
 * missing or refuses (no permission, iframe policy) → a hidden textarea +
 * execCommand. Resolves on success, rejects otherwise.
 */
function legacyCopy(text) {
  return new Promise((resolve, reject) => {
    const area = document.createElement('textarea')
    area.value = text
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.opacity = '0'
    document.body.appendChild(area)
    area.select()
    try {
      if (typeof document.execCommand === 'function' && document.execCommand('copy')) resolve()
      else reject(new Error('copy failed'))
    } catch (error) {
      reject(error)
    } finally {
      document.body.removeChild(area)
    }
  })
}

export function copyText(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text).catch(() => legacyCopy(text))
  }
  return legacyCopy(text)
}
